
"use client";

import { useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AUTH_INVALID_EMAIL, AUTH_PASSWORD_REQUIRED, AUTH_WEAK_PASSWORD } from "@/lib/auth-copy";
import { useAuth } from "@/contexts/auth-context";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { getAuthErrorMessage } from "@/lib/auth-errors";
import { trackEvent, AnalyticsEvents } from "@/lib/analytics";

const baseAuthSchema = z.object({
  email: z.string().email(AUTH_INVALID_EMAIL.description),
  password: z.string(),
});

type AuthFormData = z.infer<typeof baseAuthSchema>;

interface AuthFormProps {
  mode: "login" | "signup";
}

export function AuthForm({ mode }: AuthFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isSocialLoading, setIsSocialLoading] = useState<'google' | 'apple' | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState<{ title: string; description?: string } | null>(null);
  const { signUp, logIn, signInWithGoogle, signInWithApple } = useAuth();
  const navigate = useNavigate();

  const formSchema = useMemo(
    () =>
      baseAuthSchema.extend({
        password:
          mode === "signup"
            ? z.string().min(6, AUTH_WEAK_PASSWORD.description)
            : z.string().min(1, AUTH_PASSWORD_REQUIRED.description),
      }),
    [mode],
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AuthFormData>({
    resolver: zodResolver(formSchema),
  });

  const onSubmit = async (values: AuthFormData) => {
    setIsLoading(true);
    setFormError(null);

    try {
      if (mode === "signup") {
        await signUp(values.email, values.password);
        trackEvent(AnalyticsEvents.SIGN_UP, { method: 'email' });
      } else {
        await logIn(values.email, values.password);
        trackEvent(AnalyticsEvents.LOGIN, { method: 'email' });
      }
      navigate("/", { replace: true });
    } catch (error: any) {
      console.error("Auth error:", error);

      // Get user-friendly error message for the inline alert
      const errorMessage = getAuthErrorMessage(error, mode);

      setFormError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleGoogleSignIn = async () => {
    setIsSocialLoading('google');
    setFormError(null);

    try {
      await signInWithGoogle();
      trackEvent(AnalyticsEvents.LOGIN, { method: 'google' });
      navigate("/", { replace: true });
    } catch (error: any) {
      console.error("Google sign-in error:", error);
      const errorMessage = getAuthErrorMessage(error, 'login');
      setFormError(errorMessage);
    } finally {
      setIsSocialLoading(null);
    }
  };

  const handleAppleSignIn = async () => {
    setIsSocialLoading('apple');
    setFormError(null);

    try {
      await signInWithApple();
      trackEvent(AnalyticsEvents.LOGIN, { method: 'apple' });
      navigate("/", { replace: true });
    } catch (error: any) {
      console.error("Apple sign-in error:", error);
      const errorMessage = getAuthErrorMessage(error, 'login');
      setFormError(errorMessage);
    } finally {
      setIsSocialLoading(null);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold text-center">
          {mode === "login" ? "Welcome back" : "Create account"}
        </CardTitle>
        <CardDescription className="text-center">
          {mode === "login"
            ? "Enter your credentials to access your account"
            : "Enter your details to create your account"}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {formError && (
            <Alert variant="destructive" className="space-y-2" aria-live="polite">
              <AlertTitle>{formError.title}</AlertTitle>
              {formError.description && <AlertDescription>{formError.description}</AlertDescription>}
            </Alert>
          )}

          <div className="space-y-3">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              aria-label="Email input"
              placeholder="Enter your email"
              {...register("email")}
              disabled={isLoading}
            />
            {errors.email && (
              <Alert variant="destructive" aria-live="polite">
                <AlertTitle>{AUTH_INVALID_EMAIL.title}</AlertTitle>
                <AlertDescription>{errors.email.message}</AlertDescription>
              </Alert>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                aria-label="Password input"
                placeholder="Enter your password"
                {...register("password")}
                disabled={isLoading}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                onClick={togglePasswordVisibility}
                disabled={isLoading}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
            </div>
            {errors.password &&
              (mode === "signup" ? (
                <Alert variant="destructive" className="mt-3" aria-live="polite">
                  <AlertTitle>{AUTH_WEAK_PASSWORD.title}</AlertTitle>
                  <AlertDescription>{errors.password.message}</AlertDescription>
                </Alert>
              ) : (
                <Alert variant="destructive" className="mt-3" aria-live="polite">
                  <AlertTitle>{AUTH_PASSWORD_REQUIRED.title}</AlertTitle>
                  <AlertDescription>{AUTH_PASSWORD_REQUIRED.description}</AlertDescription>
                </Alert>
              ))}
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={isLoading}
            aria-label={mode === "login" ? "Sign in button" : "Create account button"}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {mode === "login" ? "Sign in" : "Create account"}
          </Button>

          {/* Social Sign-In Divider */}
          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">
                Or continue with
              </span>
            </div>
          </div>

          {/* Social Sign-In Buttons */}
          <div className="grid grid-cols-2 gap-3">
            <Button
              type="button"
              variant="outline"
              disabled={isLoading || isSocialLoading !== null}
              onClick={handleGoogleSignIn}
              className="w-full"
            >
              {isSocialLoading === 'google' ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
              )}
              Google
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={isLoading || isSocialLoading !== null}
              onClick={handleAppleSignIn}
              className="w-full"
            >
              {isSocialLoading === 'apple' ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                </svg>
              )}
              Apple
            </Button>
          </div>
        </form>

        <div className="mt-4 text-center text-sm">
          {mode === "login" ? (
            <>
              <p className="mb-2">
                Don't have an account?{" "}
                <Link
                  to="/signup"
                  replace
                  className="text-primary hover:underline font-medium"
                >
                  Sign up
                </Link>
              </p>
              <p>
                <Link
                  to="/reset-password"
                  className="text-primary hover:underline font-medium"
                >
                  Forgot your password?
                </Link>
              </p>
            </>
          ) : (
            <p>
              Already have an account?{" "}
              <Link
                to="/login"
                replace
                className="text-primary hover:underline font-medium"
              >
                Sign in
              </Link>
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
